import {
  Message,
  MessageFile,
  MessagesState,
  forwardMessage,
  getChatMessages,
  getMessagesWithFiles,
} from "@/entities/message";
import { User } from "@/entities/user";
import { useChatStore } from "@/entities/chat";
import {
  getTempMessagesDB,
  processFilesForMessage,
  removeTempMessageDB,
  SerializableTempMessage,
  setTempMessagesDB,
  updateLastMessage,
  uploadFiles,
} from "../lib";
import { sendWSMessage } from "@/processes/socket";
import { enqueueSnackbar } from "notistack";
import { processTempMessages } from "../lib";

// Тип для контекста (чтобы не передавать set/get напрямую)
type SetState = (
  partial:
    | MessagesState
    | Partial<MessagesState>
    | ((state: MessagesState) => Partial<MessagesState> | MessagesState)
) => void;

type MessagesContext = {
  getState: () => MessagesState;
  setState: SetState;
};

export const handleLoadMessages = async (
  context: MessagesContext,
  chatId: string,
  page = 1,
  replace = false
): Promise<void> => {
  const { getState, setState } = context;
  const state = getState();

  const currentMessages = replace ? [] : state.messages[chatId] ?? [];
  const currentCount = replace ? 0 : currentMessages.length;
  const total = replace ? undefined : state.totalCount[chatId];
  if (total !== undefined && currentCount >= total) return;

  const isFirstPage = page === 1;
  const loadingKey = isFirstPage
    ? "loadingChatMessages"
    : "loadingMoreMessages";
  if (state[loadingKey][chatId]) return;

  setState({ [loadingKey]: { ...state[loadingKey], [chatId]: true } });

  try {
    const res = await getChatMessages(chatId, page);
    if (!res?.success || !res.result) return;

    const { tempMessages, allFiles } = await processTempMessages(
      chatId,
      replace ? [] : currentMessages
    );

    const serverMessages = res.result;

    const serverTimestamps = serverMessages.map(msg => new Date(msg.createdAt).getTime());

    const chat = useChatStore.getState().getChat(chatId);

    const filteredTempMessages = tempMessages.filter((tempMsg) => {
      const tempTime = new Date(tempMsg.createdAt).getTime();

      const hasEarlier = serverTimestamps.some((ts) => ts < tempTime);
      const hasLater = serverTimestamps.some((ts) => ts > tempTime);

      if (tempMsg._id === chat?.lastMessage?._id) {
        return hasEarlier;
      }

      return hasEarlier && hasLater;
    });

    const messageMap = new Map<string | undefined, Message>();
    const pushUnique = (msg: Message) => messageMap.set(msg._id || msg.text, msg);

    [...(replace ? [] : currentMessages), ...serverMessages, ...filteredTempMessages].forEach(pushUnique);

    const uniqueMessages = Array.from(messageMap.values()).sort(
      (a, b) =>
        new Date(a.createdAt).getTime() -
        new Date(b.createdAt).getTime()
    );

    setState((state) => {
      const existingFiles = state.files[chatId] || [];

      const uniqueNewFiles = allFiles.filter(
        (newFile) =>
          !existingFiles.some((existing) => existing.msgId !== newFile.msgId)
      );

      return {
        messages: { ...state.messages, [chatId]: uniqueMessages },
        page: { ...state.page, [chatId]: page },
        pages: {
          ...state.pages,
          [chatId]: replace
            ? [page]
            : [...(state.pages[chatId] || []), page],
        },
        totalCount: { ...state.totalCount, [chatId]: res.count },
        files: {
          ...state.files,
          [chatId]: [...existingFiles, ...uniqueNewFiles],
        },
        [loadingKey]: { ...state[loadingKey], [chatId]: false },
      };
    });

  } catch (e) {
    console.error("Failed to load messages:", e);
    setState((state) => ({
      [loadingKey]: { ...state[loadingKey], [chatId]: false },
    }));
  }
};

export const handleLoadFiles = async (
  context: MessagesContext,
  chatId: string
): Promise<void> => {
  const { getState, setState } = context;
  const state = getState();

  if (state.loadingFiles[chatId]) {
    return;
  }

  setState((prevState) => ({
    loadingFiles: { ...prevState.loadingFiles, [chatId]: true },
  }));

  try {
    const res = await getMessagesWithFiles(chatId);
    if (res.success) {
      setState((prevState) => {
        const existingFiles = prevState.files[chatId] || [];

        const newFiles: MessageFile[] = res.result.flatMap((msg) =>
          (msg.fileUrls || []).map((url) => ({
            url,
            msgId: msg._id,
            createdAt: msg.createdAt,
          }))
        );

        const mergedFiles = [...newFiles, ...existingFiles];

        const sortedFiles = mergedFiles.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        return {
          files: { ...prevState.files, [chatId]: sortedFiles },
        };
      });
    }
    else {
      console.error(
        "Failed to load files for chat",
        chatId,
        ":",
        res.error
      );
    }
  } catch (error) {
    console.error("Error loading files for chat", chatId, ":", error);
  } finally {
    setState((prevState) => ({
      loadingFiles: { ...prevState.loadingFiles, [chatId]: false },
    }));
  }
};

const sendOrResendMessage = async (
  context: MessagesContext,
  chatId: string,
  receiverId: string,
  owner: User,
  content: string,
  files: File[],
  fileUrls: string[],
  replyingMessage?: Message | null,
  originalMessageId?: string
): Promise<void> => {
  console.log("sdfasfssendnsend");
  
  const { getState, setState } = context;
  const time = Date.now();
  const tempId = originalMessageId || `temp-${time}`;

  // Конвертируем файлы в base64 для сохранения
  const { filesBase64, blobUrls } = await processFilesForMessage(files);

  const urls = fileUrls?.length > 0 ? fileUrls : blobUrls;

  // Формируем сообщение
  const tempMessage: Message = {
    _id: tempId,
    chat: chatId as any,
    owner,
    text: content,
    fileUrls: urls,
    files,
    createdAt: time,
    sentAt: time,
    name: "You",
    type: files.length ? "file" : "text",
    reply: replyingMessage || undefined,
  };

  const tempMessageForStorage: SerializableTempMessage = {
    ...tempMessage,
    files: undefined,
    filesBase64: filesBase64.length ? filesBase64 : undefined,
  };

  // --- Основная логика: добавляем или заменяем ---
  const tempMessagesAll = await getTempMessagesDB();
  const oldList = tempMessagesAll[chatId] || [];

  if (originalMessageId) {
    // RESEND: просто заменяем сообщение по ID
    const updatedStored = oldList.map((msg) =>
      msg._id === originalMessageId
        ? { ...tempMessageForStorage, _id: originalMessageId }
        : msg
    );
    await setTempMessagesDB(chatId, updatedStored);

    setState((state) => {
      const chatMessages = state.messages[chatId] || [];
      const updatedMessages = chatMessages.map((m) =>
        m._id === originalMessageId ? tempMessage : m
      );

      const currentFiles = state.files[chatId] || [];

      // === используем MessageFile ===
      const newFiles: MessageFile[] = urls.map((url) => ({
        url,
        msgId: tempId,
        createdAt: time,
      }));

      const mergedFiles = [
        ...currentFiles,
        ...newFiles.filter(
          (nf) => !currentFiles.some((f) => f.url === nf.url && f.msgId === nf.msgId)
        ),
      ];

      return {
        messages: { ...state.messages, [chatId]: updatedMessages },
        files: { ...state.files, [chatId]: mergedFiles },
        loadingMessage: { ...state.loadingMessage, [originalMessageId]: true },
      };
    });
  } else {
    // SEND: добавляем новое сообщение
    const updatedForChat = [...oldList, tempMessageForStorage];
    await setTempMessagesDB(chatId, updatedForChat);

    setState((state) => {
      const currentFiles = state.files[chatId] || [];

      const newFiles: MessageFile[] = urls.map((url) => ({
        url,
        msgId: tempId,
        createdAt: time,
      }));

      const mergedFiles = [
        ...currentFiles,
        ...newFiles.filter(
          (nf) => !currentFiles.some((f) => f.url === nf.url && f.msgId === nf.msgId)
        ),
      ];

      return {
        messages: {
          ...state.messages,
          [chatId]: [...(state.messages[chatId] || []), tempMessage],
        },
        files: { ...state.files, [chatId]: mergedFiles },
        loadingMessage: { ...state.loadingMessage, [tempId]: true },
      };
    });
  }

  updateLastMessage(chatId, tempMessage);

  // Отправка без файлов
  if (!files.length) {
    sendWSMessage("message:send", {
      chat: chatId,
      text: content,
      sentAt: tempMessage.sentAt,
      receiver: receiverId,
      reply: replyingMessage?._id,
    });
    return;
  }

  const { successUrls, failedFiles } = await uploadFiles(chatId, files);

  console.log(chatId);
  
  if (failedFiles.length > 0) {
    const errorMessage = { ...tempMessage, err: true, errFiles: failedFiles };

    setState((state) => ({
      messages: {
        ...state.messages,
        [chatId]: state.messages[chatId].map((m) =>
          m._id === tempId ? errorMessage : m
        ),
      },
      loadingMessage: { ...state.loadingMessage, [tempId]: false },
    }));

    updateLastMessage(chatId, errorMessage);
  }

  if (successUrls.length > 0) {
    const payload: any = {
      chat: chatId,
      sentAt: tempMessage.sentAt,
      receiver: receiverId,
      reply: replyingMessage?._id,
      fileUrls: successUrls,
    };

    if (content.trim() !== "") {
      payload.text = content;
    }

    sendWSMessage("message:send", payload);
  }
};

export const handleSendMessage = async (
  context: MessagesContext,
  content: string,
  owner: User,
  chatId: string,
  receiverId: string,
  replyingMessage?: Message | null,
  editingMessage?: Message | null,
  files: File[] = []
): Promise<void> => {
  await sendOrResendMessage(
    context,
    chatId,
    receiverId,
    owner,
    content,
    files,
    [],
    replyingMessage
  );
};

export const handleResendMessage = async (
  context: MessagesContext,
  message: Message,
  owner: User,
  chatId: string,
  receiverId: string
): Promise<void> => {
  await sendOrResendMessage(
    context,
    chatId,
    receiverId,
    owner,
    message.text || "",
    message.files || [],
    message.fileUrls || [],
    message.reply || undefined,
    message._id
  );
};

export const handleUpdateMessage = async (
  context: MessagesContext,
  chatId: string,
  messageId: string,
  newText: string,
  newFiles: File[],
  oldFiles: string[]
): Promise<void> => {
  const { getState, setState } = context;
  const state = getState();
  const currentMessages = state.messages[chatId] || [];
  const originalMessage = currentMessages.find((msg) => msg._id === messageId);

  if (!originalMessage) {
    console.warn("Сообщение для редактирования не найдено:", messageId);
    return;
  }

  // Сохраняем копию оригинального сообщения для отката
  const originalMessageCopy = { ...originalMessage };

  const time = Date.now();
  const owner = originalMessage.owner;
  const actualNewFiles = newFiles || [];

  // Конвертируем новые файлы в base64 и blobUrls
  const { filesBase64: newFilesBase64, blobUrls: newBlobUrls } =
    await processFilesForMessage(actualNewFiles);

  // Формируем обновлённое сообщение (временно)
  const updatedTempMessage: Message = {
    ...originalMessage,
    text: newText,
    files: actualNewFiles,
    fileUrls: [...(oldFiles || []), ...newBlobUrls],
    filesBase64: newFilesBase64.length > 0 ? newFilesBase64 : undefined,
    sentAt: time,
    type: actualNewFiles.length > 0 || oldFiles?.length ? "file" : "text",
  };

  // Обновляем в стейте (временно — показываем загрузку)
  const updatedMessages = currentMessages.map((msg) =>
    msg._id === messageId ? updatedTempMessage : msg
  );

  let updatedFilesList = state.files[chatId] || [];

  // Удаляем старые URL
  // if (originalMessage.fileUrls?.length) {
  //   const oldUrls = new Set(originalMessage.fileUrls);
  //   updatedFilesList = updatedFilesList.filter((url) => !oldUrls.has(url));
  //   originalMessage.fileUrls.forEach(URL.revokeObjectURL);
  // }

  // Добавляем новые URL
  const newUniqueUrls = newBlobUrls.filter(
    (url) => !updatedFilesList.some((file) => file.url === url)
  );

  updatedFilesList = [...updatedFilesList, ...newUniqueUrls.map((url) => ({ url, msgId: messageId, createdAt: time }))];

  setState({
    messages: { ...state.messages, [chatId]: updatedMessages },
    files: { ...state.files, [chatId]: updatedFilesList },
    loadingMessage: { ...state.loadingMessage, [messageId]: true },
  });

  updateLastMessage(chatId, updatedTempMessage);

  if (actualNewFiles.length === 0) {
    sendWSMessage("message:update", {
      chat: chatId,
      message: messageId,
      text: newText.trim() === "" ? undefined : newText,
      fileUrls: [...(oldFiles || []), ...newBlobUrls],
      time,
    });

    setState((s) => ({
      messages: {
        ...s.messages,
        [chatId]: s.messages[chatId].map((m) =>
          m._id === messageId ? { ...m, err: false, sentAt: time } : m
        ),
      },
      loadingMessage: { ...s.loadingMessage, [messageId]: false },
    }));
    return;
  }

  // --- Загрузка файлов ---
  const { successUrls, failedFiles } = await uploadFiles(
    chatId,
    actualNewFiles
  );

  if (failedFiles.length > 0) {
    // Ошибка загрузки → откатываем всё к оригиналу

    // Восстанавливаем оригинальное сообщение
    const revertedMessages = currentMessages.map((msg) =>
      msg._id === messageId ? originalMessageCopy : msg
    );

    // Восстанавливаем файлы (возвращаем старые URL, удаляем новые blob-URL)
    let revertedFilesList = state.files[chatId] || [];

    // Удаляем временные blob-URL, созданные для новых файлов
    // newBlobUrls.forEach(URL.revokeObjectURL);
    revertedFilesList = revertedFilesList.filter(
      (file) => !newUniqueUrls.includes(file.url)
    );

    // Возвращаем старые URL, если они были удалены
    if (originalMessageCopy.fileUrls?.length) {

      const missingOldUrls = originalMessageCopy.fileUrls.filter(
        (url) => !revertedFilesList.some((file) => file.url === url)
      );

      revertedFilesList = [...revertedFilesList, ...missingOldUrls.map((url) => ({ url, msgId: messageId, createdAt: time }))];
    }

    setState({
      messages: { ...state.messages, [chatId]: revertedMessages },
      files: { ...state.files, [chatId]: revertedFilesList },
      loadingMessage: { ...state.loadingMessage, [messageId]: false },
    });

    updateLastMessage(chatId, originalMessageCopy);

    enqueueSnackbar(`Не удалось загрузить файлы: ${failedFiles.join(", ")}`, {
      variant: "error",
    });

    return;
  }

  const payload: any = {
    chat: chatId,
    message: messageId,
    fileUrls: [...(oldFiles || []), ...successUrls],
    time,
  };

  if (newText.trim() !== "") {
    payload.text = newText;
  }

  sendWSMessage("message:update", payload);

  const finalMessage = {
    ...updatedTempMessage,
    err: false,
    // fileUrls: successUrls,
    files: [],
    sentAt: time,
  };

  // setState((s) => ({
  //   messages: {
  //     ...s.messages,
  //     [chatId]: s.messages[chatId].map((m) =>
  //       m._id === messageId ? finalMessage : m
  //     ),
  //   },
  //   loadingMessage: { ...s.loadingMessage, [messageId]: false },
  // }));

  updateLastMessage(chatId, finalMessage);
};

export const handleSendReadedMessages = (
  context: MessagesContext,
  chatId: string,
  lastReadMessageId: string,
  currentUserId: string
): void => {
  const { getState, setState } = context;
  const chatMessages = getState().messages[chatId] || [];
  
  if (chatMessages.length === 0) return;

  const sortedMessages = [...chatMessages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const targetIndex = sortedMessages.findIndex(
    (msg) => msg._id === lastReadMessageId
  );
  if (targetIndex === -1) return;

  const time = Date.now();
  const updatedMessages = sortedMessages.map((msg, idx) => {
    if (
      msg.owner._id === currentUserId ||
      msg.readedAt != null ||
      idx > targetIndex
    ) {
      return msg;
    }
    return { ...msg, readedAt: time };
  });

  setState({
    messages: { ...getState().messages, [chatId]: updatedMessages },
  });

  sendWSMessage("message:readed", {
    chat: chatId,
    message: lastReadMessageId,
    time,
  });
};

export const handleMarkMessagesAsRead = (
  context: MessagesContext,
  chatId: string,
  upToMessageId: string,
  currentUserId: string
): { lastReadMessageId: string | null; unreadCount: number } | null => {
  const { getState, setState } = context;
  const state = getState();
  const chatMessages = state.messages[chatId] || [];

  if (!chatMessages.length) return null;

  const sortedMessages = [...chatMessages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const targetMsg = sortedMessages.find((msg) => msg._id === upToMessageId);
  if (!targetMsg || targetMsg.owner._id === currentUserId) {
    return null;
  }

  const lastReadMessageId = state.lastReadMessageIds?.[chatId] ?? null;

  if (lastReadMessageId) {
    const lastReadMsg = sortedMessages.find(
      (msg) => msg._id === lastReadMessageId
    );
    if (
      lastReadMsg &&
      new Date(lastReadMsg.createdAt) >= new Date(targetMsg.createdAt)
    ) {
      return null;
    }
  }

  const newLastReadMessageId = upToMessageId;
  const targetIndex = sortedMessages.findIndex(
    (msg) => msg._id === upToMessageId
  );
  const unreadCount = sortedMessages
    .slice(targetIndex + 1)
    .filter((msg) => msg.owner._id !== currentUserId).length;

  setState({
    lastReadMessageIds: {
      ...state.lastReadMessageIds,
      [chatId]: newLastReadMessageId,
    },
  });

  return { lastReadMessageId: newLastReadMessageId, unreadCount };
};

export const handleDeleteMessage = async (
  context: MessagesContext,
  chatId: string,
  messageIds: string[]
): Promise<void> => {
  const { getState, setState } = context;
  const state = getState();

  const currentMessages = state.messages[chatId] || [];
  const messageToDelete: Message[] = currentMessages.filter((msg) => messageIds.includes(msg._id));

  if (!messageToDelete.length) {
    console.warn("Сообщения для удаления не найдено:", messageIds);
    return;
  }

  const isTempMessage =
    messageToDelete[0]._id.startsWith("temp-") || messageToDelete[0].err;

  if (isTempMessage) {
    const updatedMessages = currentMessages.filter(
      (msg) => !messageIds.includes(msg._id)
    );

    if (messageToDelete[0].sentAt) {
      await removeTempMessageDB(chatId, messageToDelete[0].sentAt);
    }

    let updatedFiles = state.files[chatId] || [];
    if (messageToDelete[0].fileUrls?.length) {
      const urlsToDelete = new Set(messageToDelete[0].fileUrls);
      updatedFiles = updatedFiles.filter((file) => !urlsToDelete.has(file.url));
      messageToDelete[0].fileUrls.forEach(URL.revokeObjectURL);
    }

    setState({
      messages: { ...state.messages, [chatId]: updatedMessages },
      files: { ...state.files, [chatId]: updatedFiles },
    });

    const chatStoreState = useChatStore.getState();
    const chatInStore = chatStoreState.chats.find((c) => c._id === chatId);
    const isLastMessage = chatInStore?.lastMessage?._id ? messageIds.includes(chatInStore.lastMessage._id) : false;

    if (isLastMessage) {
      const newLastMessage =
        updatedMessages.length > 0
          ? updatedMessages[updatedMessages.length - 1]
          : undefined;

      updateLastMessage(chatId, newLastMessage);
    }

    return;
  }

  setState({
    loadingMessage: { ...state.loadingMessage, ...messageIds.reduce((acc, messageId) => ({ ...acc, [messageId]: true }), {}) },
  });

  sendWSMessage("message:delete", {
    messages: messageIds,
    chat: chatId,
  });
};


export const handleForwardMessage = async (
  context: any,
  fromChatId: string,
  messagesIds: string[],
  toChatIds: string[],
) => {
  const { setState, getState } = context;

  try {
    const res = await forwardMessage(fromChatId, messagesIds, toChatIds);

    if (res.success) {
      const forwardedMessages: Message[] = res.result;

      const messagesByChatId: Record<string, Message[]> = {};
      forwardedMessages.forEach(msg => {
        const chatId = typeof msg.chat === "string" ? msg.chat : msg.chat._id;
        if (!messagesByChatId[chatId]) messagesByChatId[chatId] = [];
        messagesByChatId[chatId].push(msg);
      });

      setState((state: any) => {
        const newMessagesState = { ...state.messages };
        const newLoadingState = { ...state.loadingMessage };

        Object.entries(messagesByChatId).forEach(([chatId, msgs]) => {
          const existingMessages = newMessagesState[chatId] || [];
          newMessagesState[chatId] = [...existingMessages, ...msgs];
          newLoadingState[chatId] = false;

          updateLastMessage(chatId, msgs[msgs.length - 1]);
        });

        return {
          messages: newMessagesState,
          loadingMessage: newLoadingState,
        };
      });

      if (toChatIds.length === 1) {
        const chatId = toChatIds[0];
        setTimeout(() => {
          window.location.href = `/?chat=${chatId}`;
        }, 150); 
      }
    }
  } catch (error) {
    console.error("Ошибка при пересылке сообщения:", error);
  }
};
